import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { fetchAdoptablePetDetails } from "./service";
import { getYearsAndMonths, isNullOrEmpty } from "./util.ts";
import AdoptablePetDetailSlideshow, {
  SlideshowImage,
} from "./AdoptablePetDetailSlideshow.tsx";
import Pet404 from "./Pet404.tsx";
import CustomLightbox from "./CustomLightbox";
import "yet-another-react-lightbox/styles.css";
import { ShowPets } from "./ShowPets.tsx";
import shareIconFB from "./assets/images/share_fb.svg";
import shareIconInsta from "./assets/images/share_insta.svg";
import shareIconEmail from "./assets/images/share_email.svg";
// import Lightbox from "yet-another-react-lightbox";

export default function AdoptablePetDetailPage() {
  const [petId, setPetId] = useState<string>();
  const [tempPetId, setTempPetId] = useState<string>();
  useEffect(() => {
    // const searchParams = new URLSearchParams(window.location.search);
    // const id = searchParams.get("petId");
    // Extract the ID from the URL path
    const path = window.location.pathname;
    const segments = path.split('/'); // Split the path into segments
    const id = segments[segments.length - 1]; // Get the last segment
    console.log("initializing with pet id:", id);
    if (isNullOrEmpty(id)) return;
    setPetId(id);
    setTempPetId(id);
  }, []);
  return (
    <>
      <div>
        <input
          value={tempPetId}
          onChange={(e) => setTempPetId(e.target.value)}
        />
        <br />
        <button onClick={() => setPetId(tempPetId)}>Go</button>
      </div>
      <AdoptablePetDetailWrapper petId={petId} />
    </>
  );
}

type PetDetailWrapperProps = {
  petId: string | number;
};

export function AdoptablePetDetailWrapper({ petId }: PetDetailWrapperProps) {
  const [petDetails, setPetDetails] = useState(undefined);

  useEffect(() => {
    (async () => {
      setPetDetails(undefined);
      const result = await fetchAdoptablePetDetails(petId);
      setPetDetails(result);
    })();
  }, [petId]);

  if (petDetails === undefined) {
    // show some loading state
    return <Skeleton count={5} />;
  }

  return <PetDetail petDetails={petDetails} />;
}

type PetDetailProps = {
  petDetails: any;
};

// Not used any more???
// function getAllPetsPath(species: string) {
//   const speciesLower = species.toLowerCase();
//   switch (speciesLower) {
//     case 'dog': return PAGE_PATHS.ALL_DOGS;
//     case 'cat': return PAGE_PATHS.ALL_CATS;
//     default: return PAGE_PATHS.ALL_CRITTERS;
//   }
// }

export function PetDetail({ petDetails }: PetDetailProps) {
  // added by DML David to handle the 500 server error that gives us a blank page
  if (petDetails === null) {
    return <Pet404 />;
  }

  const images: SlideshowImage[] = [];
  {
    const alt = `Photo of ${petDetails.adoptableDetails.animalName}`;
    let i = 1;
    while (petDetails.adoptableDetails[`photo${i}`]) {
      if (/\S/.test(petDetails.adoptableDetails[`photo${i}`])) {
        images.push({
          src: petDetails.adoptableDetails[`photo${i}`],
          alt,
          key: i,
        });
      }
      i++;
    }
    // check if the pet has a video
    if (/^[a-zA-Z0-9_-]+$/.test(petDetails.adoptableDetails.videoId)) {
      images.push({
        videoId: petDetails.adoptableDetails.videoId,
        alt,
        key: 99,
      });
    }
  }

  // Create list for lighbox
  // Is this DRY? I think not...
  let lightBoxImages = [];
  const alt = `Photo of ${petDetails.adoptableDetails.animalName}`;
  let i = 1;
  while (petDetails.adoptableDetails[`photo${i}`]) {
    if (/\S/.test(petDetails.adoptableDetails[`photo${i}`])) {
      lightBoxImages.push({
        src: petDetails.adoptableDetails[`photo${i}`],
        alt,
        type: "iamge",
        key: i,
      });
    }
    i++;
  }
  if (/^[a-zA-Z0-9]+$/.test(petDetails.adoptableDetails.videoId)) {
    lightBoxImages.push({
      src: `https://www.youtube.com/embed/${petDetails.adoptableDetails.videoId}`,
      alt: `Video of ${petDetails.adoptableDetails.animalName}`,
      type: "video",
      key: 99, // TODO this is sorta ridic but works for now (because there's only 1 video for now)
    });
  }

  const [open, setOpen] = useState(false); // state for the lightbox

  // stuff for the email mailto share link
  const emailSubject = `Take a look at ${petDetails.adoptableDetails.animalName}'s profile`;
  const emailAddress = "";
  const emailBody = `
Hi there!

I thought you’d enjoy seeing this adorable pet on the Seattle Humane website. Here’s a quick peek at ${petDetails.adoptableDetails.animalName}:

https://www.seattlehumane.org/pet-detail/?pet=${petDetails.adoptableDetails.id}

Let me know what you think!

Cheers,
[Your Name]
`;
  const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  const fbUrlParams = new URLSearchParams();
  fbUrlParams.set(
    "u",
    `https://www.seattlehumane.org/pet-detail/?pet=${petDetails.adoptableDetails.id}`,
  );
  const fbShareLinkUrl = `https://www.facebook.com/sharer/sharer.php?${fbUrlParams.toString()}`;

  return (
    <>
      <CustomLightbox
        open={open}
        close={() => setOpen(false)}
        slides={lightBoxImages}
      />
      <div className="flex flex-col items-center gap-4">
        <AdoptablePetDetailSlideshow
          images={images}
          setOpen={setOpen}
          open={open}
        />

        <div
          className="text-center text-[3rem] sm:text-[3.9rem] text-primary-dark mr-2 mb-4"
          style={{ fontFamily: "Varela Round", lineHeight: "3.5rem" }}
        >
          Meet {petDetails.adoptableDetails.animalName}
        </div>

        <section className="flex flex-wrap gap-4 justify-center items-start mb-8">
          <div className="grid grid-cols-full_full px-4 sm:grid-cols-[180px_220px] lg:grid-cols-[140px_160px] mb-4">
            {petDetails.adoptableDetails.species.toLowerCase() !== "cat" &&
              petDetails.adoptableDetails.species.toLowerCase() !== "dog" && (
                <>
                  <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
                    Species
                  </div>
                  <div className="border-b-[1px] mb-2 text-wrap">
                    {petDetails.adoptableDetails.species}
                  </div>
                </>
              )}
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Looks Like
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {petDetails.adoptableDetails.primaryBreed}&nbsp;
              {petDetails.adoptableDetails.secondaryBreed}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Age
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {getYearsAndMonths(petDetails.adoptableDetails.age)}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Weight
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {petDetails.adoptableDetails.bodyWeight}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Gender
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {petDetails.adoptableDetails.sex}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Color
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {petDetails.adoptableDetails.primaryColor}&nbsp;
              {petDetails.adoptableDetails.secondaryColor}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Location
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {petDetails.adoptableDetails.location}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Animal ID
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {petDetails.adoptableDetails.id}
            </div>
          </div>

          <div className="description w-full px-4 sm:px-2 sm:w-[600px] md:w-[600px] md:pl-8 break-words">
            <h3 className="text-2xl font-bold mb-8">About Me</h3>
            {petDetails.adoptableDetails.dsc &&
            petDetails.adoptableDetails.dsc.trim() !== "" ? (
              <p
                dangerouslySetInnerHTML={{
                  __html: petDetails.adoptableDetails.dsc,
                }}
              />
            ) : (
              <p>Stay tuned for more details about me!</p>
            )}
            <div className="mb-4" />

            <div className="mb-8">
              <h4 className="text-xl font-bold text-primary-dark mb-2">
                Share my profile
              </h4>

              <div className="share-icons">
                <div className="mb-4">
                  <a
                    className="hover:text-red-600 no-underline"
                    href={fbShareLinkUrl}
                  >
                    <img
                      src={shareIconFB}
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                        filter:
                          "invert(32%) sepia(22%) saturate(6785%) hue-rotate(342deg) brightness(111%) contrast(88%)",
                        display: "inline-block",
                      }}
                    />
                    <span>Post my profile on Facebook</span>
                  </a>
                </div>
                <div className="mb-4">
                  <a
                    className="hover:text-red-600 no-underline"
                    href={"https://www.instagram.com/seattlehumane/"}
                  >
                    <img
                      src={shareIconInsta}
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                        filter:
                          "invert(32%) sepia(22%) saturate(6785%) hue-rotate(342deg) brightness(111%) contrast(88%)",
                        display: "inline-block",
                      }}
                    />
                    <span>Join us on Instagram</span>
                  </a>
                </div>
                <div className="mb-4">
                  <a
                    className="hover:text-red-600 no-underline"
                    href={mailtoLink}
                  >
                    <img
                      src={shareIconEmail}
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                        filter:
                          "invert(32%) sepia(22%) saturate(6785%) hue-rotate(342deg) brightness(111%) contrast(88%)",
                        display: "inline-block",
                      }}
                    />
                    <span>
                      Email a friend {petDetails.adoptableDetails.animalName}'s
                      profile
                    </span>
                  </a>
                </div>
              </div>

              <div className="mb-4">
                {/* <a href="#XWMTYMCE" style={{display: "none"}} /> */}
                <a href="#XULMGXQU" style={{ display: "none" }} />
              </div>
            </div>

            <p className="questionnaire font-bold align-center">
              Not all details about a pet's adoption needs are listed here.
              Additional information about the pet will be shared when you visit
              us during our walk-in hours, no appointment needed.
            </p>
          </div>
        </section>
      </div>
      <div className="mb-8" /> {/* spacer */}
      <ShowPets
        petType={petDetails.adoptableDetails.species.toLowerCase()}
        display={"carousel"}
        title={`More ${petDetails.adoptableDetails.species}s`}
        // allPetsLink={getAllPetsPath(petDetails.adoptableDetails.species)}
      />
      {/* <pre className="bg-slate-100 overflow-x-scroll"><code>
    {JSON.stringify(petDetails, undefined, 2)}
  </code></pre>  */}
    </>
  );
}
